import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { styled } from '@noshiro/goober';
import { memoNamed, useState } from '@noshiro/preact-utils';
import { useResizeObserver } from '@noshiro/resize-observer-preact-hooks';
import { useMedia } from 'preact-media-hook';
import { useEffect, useMemo } from 'preact/hooks';
import { mediaQueries } from '../../constants';
const tabWidthSmallPx = 72;
const tabWidthMediumPx = 160;
export const MuiTabs = memoNamed('MuiTabs', ({ labels, tabIndex, scrollable = false, tabIndexChange }) => {
    const { matches: mobile } = useMedia(mediaQueries.small);
    // const mobile = false;
    const tabWidthPx = mobile ? tabWidthSmallPx : tabWidthMediumPx;
    const { state: tabWidthList, updateState: updateTabWidthList } = useState([]);
    const tabWidthListWithDefault = useMemo(() => labels.map((_, index) => { var _a; return (_a = tabWidthList[index]) !== null && _a !== void 0 ? _a : tabWidthPx; }), [tabWidthList, tabWidthPx, labels]);
    const tabWidthAccumulated = useMemo(() => IList.scan(tabWidthListWithDefault, (acc, curr) => acc + curr, 0), [tabWidthListWithDefault]);
    const labelsWithHandler = useMemo(() => labels.map((label, index) => ({
        index,
        label,
        onClick: () => {
            tabIndexChange(index);
        },
        onResize: (width) => {
            updateTabWidthList((prev) => IList.set(prev, index, width));
        },
    })), [labels, tabIndexChange, updateTabWidthList]);
    return (_jsx(Root, { children: _jsxs(TabsScroller, Object.assign({ scrollable: scrollable }, { children: [_jsx(FlexContainer, Object.assign({ role: 'tablist' }, { children: labelsWithHandler.map(({ index, label, onClick, onResize }) => (_jsx(Tab, { label: label, selected: index === tabIndex, tabWidthPx: tabWidthPx, onClick: onClick, onResize: onResize }, index))) })), _jsx(TabIndicator, { tabIndex: tabIndex, tabWidthAccumulated: tabWidthAccumulated, tabWidthList: tabWidthListWithDefault, tabWidthPx: tabWidthPx })] })) }));
});
const Root = styled('div') `
  display: flex;
  overflow: hidden;
  min-height: 48px;
  font-size: large;
`;
const TabsScroller = memoNamed('TabsWrapper', ({ scrollable, children }) => scrollable ? (_jsx(TabScrollerStyledScrollable, { children: children })) : (_jsx(TabsScrollerBaseStyled, { children: children })));
const TabsScrollerBaseStyled = styled('div') `
  margin-bottom: 0px;
  flex: 1 1 auto;
  display: inline-block;
  position: relative;
  white-space: nowrap;
`;
const TabScrollerStyledScrollable = styled(TabsScrollerBaseStyled) `
  overflow-x: auto;
`;
const FlexContainer = styled('div') `
  display: flex;
`;
const Tab = memoNamed('Tab', ({ label, selected, tabWidthPx, onClick, onResize }) => {
    const style = useMemo(() => ({
        minWidth: `${tabWidthPx}px`,
        color: selected ? '#3f51b5' : undefined,
    }), [selected, tabWidthPx]);
    const [{ width }, ref] = useResizeObserver();
    useEffect(() => {
        if (width > 0) {
            onResize(width);
        }
    }, [width, onResize]);
    return (_jsx("div", Object.assign({ ref: ref }, { children: _jsx(TabStyled, Object.assign({ "aria-selected": selected, role: 'tab', style: style, type: 'button', onClick: onClick }, { children: label })) })));
});
const TabStyled = styled('button') `
  color: inherit;
  border: 0;
  cursor: pointer;
  margin: 0;
  display: inline-flex;
  outline: 0;
  padding: 0;
  position: relative;
  align-items: center;
  user-select: none;
  border-radius: 0;
  vertical-align: middle;
  -moz-appearance: none;
  justify-content: center;
  text-decoration: none;
  background-color: transparent;
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;

  padding: 6px 12px;
  overflow: hidden;
  position: relative;
  font-size: 0.875rem;
  max-width: 264px;
  box-sizing: border-box;
  min-height: 48px;
  text-align: center;
  flex-shrink: 0;
  font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-weight: 500;
  line-height: 1.75;
  white-space: normal;
  letter-spacing: 0.02857em;

  color: rgba(0, 0, 0, 0.54);
`;
const TabIndicator = memoNamed('TabIndicator', ({ tabWidthAccumulated, tabWidthList, tabIndex, tabWidthPx }) => {
    var _a, _b;
    return (_jsx(TabIndicatorStyled, { style: {
            left: `${(_a = tabWidthAccumulated[tabIndex]) !== null && _a !== void 0 ? _a : 0}px`,
            width: `${(_b = tabWidthList[tabIndex]) !== null && _b !== void 0 ? _b : tabWidthPx}px`,
        } }));
});
const TabIndicatorStyled = styled('span') `
  bottom: 0;
  height: 2px;
  position: absolute;
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

  background-color: #3f51b5;
`;
